import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { Autocomplete, Button, Dialog, Divider, Grid, TextField, Typography, useTheme } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";

import DialogTitle from "../../DialogTitle/DialogTitle";
import DialogActions from "../../DialogActions/DialogActions";

import { TnAWorkedHoursDTO, TnAWorkedHoursSTO } from "../../../models/tna";
import { CustomDate } from "../../../models/custom-date/custom-date";

import { isValidDate } from "../../../utils/time-utils";

import { Props, State } from "./interfaces";
import { DialogContent } from "./styles";
import { saveWorkedHours } from "../../../services/tna-worked-hours/tna-worked-hours-api.service";
import AutocompleteDropdown from "../../../reusable-components/AutocompleteDropdown/AutocompleteDropdown";
import { LegalEntityProjectsApiService } from "../../../services/legal-entity-projects/legal-entity-projects-api.service";
import { LegalEntityProjectsFilter } from "../../../models/legal-entity-projects/legal-entity-projects-filter";
import { LegalEntityClientsApiService } from "../../../services/legal-entity-clients/legal-entity-clients-api.service";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { ClockPlusIcon } from "src/assets/icons";
import CustomConfirmationChangesDialog from "src/containers/CustomConfirmationChangesDialog/CustomConfirmationChangesDialog";
import { intlFormatDateShort } from "../../../utils/time-utils";
import ConfirmationChangesDialog from "src/containers/ConfirmationChangesDialog/ConfirmationChangesDialog";

const AddEditWorkedHours = ({
  employee,
  item,
  project,
  client,
  notifyParent,
  tnADate,
  amount,
  children,
  minDate,
  maxDate,
  buttonStyle,
  buttonType = "text",
  viewMode = false,
  dayData,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_worked_hours";
  const theme = useTheme();

  const DEFAULT_WORKED_HOURS: TnAWorkedHoursSTO = useMemo(
    () => ({
      id: "",
      isDeleted: false,
      companyAccount: item?.employee?.legalEntity?.companyAccount ?? employee?.legalEntity?.companyAccount ?? null,
      legalEntity: item?.employee?.legalEntity ?? employee?.legalEntity ?? null,
      employee: employee ?? null,
      project: project ?? null,
      client: client ?? null,
      amount: amount ?? 0,
      tnADate: tnADate ?? CustomDate.FromDate(new Date()),
      isAuto: false,
    }),
    [item, client, employee, project, tnADate, amount]
  );

  const [state, setState] = useState<State>({
    openDialog: false,
    loading: false,
    item: item ?? (DEFAULT_WORKED_HOURS as TnAWorkedHoursDTO),
    error: "",
    // workedHours: { ...DEFAULT_WORKED_HOURS },
    isWarningCCCDOpen: false,
    diiferenceInSchedule: 0,
    CCDOpen: false,
  });

  const setOpenDialog = (value: boolean) => setState((state) => ({ ...state, openDialog: value }));

  //#region change field
  const handleChangeField = (field: keyof TnAWorkedHoursSTO, value: TnAWorkedHoursSTO[keyof TnAWorkedHoursSTO]) => {
    setState((state) => ({
      ...state,
      item: { ...state.item, [field]: value, isAuto: false },
    }));
  };
  //#endregion

  //#region save worked hours
  const handleSaveWorkedHours = useCallback(
    async (isDeleted?: boolean) => {
      setState((state) => ({ ...state, loading: true }));

      try {
        const { error } = await saveWorkedHours({
          ...state.item,
          ...(!item && {
            id: uuidv4(),
          }),
          isAuto: false,
          isDeleted: isDeleted ?? false,
        });

        if (!error) {
          setState((state) => ({ ...state, openDialog: false, isWarningCCCDOpen: false, diiferenceInSchedule: 0 }));
          notifyParent();
        }
      } catch (error) {
        setState((state) => ({ ...state, error: error as string }));
      } finally {
        setState((state) => ({ ...state, loading: false }));
      }
    },
    [notifyParent, state.item, item]
  );

  const dataForThisDay = useMemo(() => {
    const year = state.item.tnADate?.year ?? new Date().getFullYear();
    const month = state.item.tnADate?.month ?? new Date().getMonth() + 1;
    const day = state.item.tnADate?.day ?? new Date().getDate();

    const dayDataForThisDay = dayData?.find(
      (_item) => _item?.tnADate?.year === year && _item?.tnADate?.month === month && _item?.tnADate?.day === day
    );

    const alreadyReportedWorkedHours =
      dayDataForThisDay?.workedHours
        ?.filter((workedHour) => workedHour?.id !== item?.id)
        ?.reduce((acc, curr) => acc + (curr?.amount ?? 0), 0) ?? 0;

    return {
      expectedPresence: dayDataForThisDay?.expectedPresence ?? 0,
      alreadyReportedWorkedHours: alreadyReportedWorkedHours,
      reportedWorkedHoursData:
        dayDataForThisDay?.workedHours?.filter((workedHour) => workedHour?.id !== item?.id) ?? [],
    };
  }, [dayData, state.item.tnADate, item?.id]);

  return (
    <>
      {!!item ? (
        <div onClick={() => setOpenDialog(true)}>{children}</div>
      ) : (
        <Button
          onClick={() => setOpenDialog(true)}
          style={{ ...buttonStyle }}
          title={!!item ? "Edit worked hours" : "Add worked hours"}
        >
          {buttonType === "icon" ? <ClockPlusIcon /> : `${t(`${tRP}.title`)}`}
        </Button>
      )}
      <Dialog
        open={state.openDialog}
        // onClose={handleCloseDialog}
        fullWidth
      >
        <DialogTitle
          title={!!item ? t(`${tRP}.edit_title`) : t(`${tRP}.title`)}
          handleClose={
            () =>
              setState(() => ({
                ...state,
                CCDOpen: true,
              }))
            // handleCloseDialog
          }
        />
        <DialogContent>
          <Autocomplete
            aria-required={true}
            disabled
            value={state.item.employee}
            getOptionLabel={(option) => option?.name ?? ""}
            options={[state.item.employee]}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${t(`common_words.employee`)}`}
              />
            )}
          />
          <AutocompleteDropdown
            labelKey={`common_words.client`}
            field="client"
            serviceApi={new LegalEntityClientsApiService()}
            propsItem={state.item}
            handleChange={handleChangeField}
            sxProps={{ maxWidth: "300px" }}
            propsFilter={
              {
                legalEntityIds: [state.item.employee?.legalEntity?.id],
              } as LegalEntityProjectsFilter
            }
            disabled={viewMode}
          />
          <AutocompleteDropdown
            labelKey={`common_words.project`}
            field="project"
            serviceApi={new LegalEntityProjectsApiService()}
            propsItem={state.item}
            handleChange={handleChangeField}
            sxProps={{ maxWidth: "300px" }}
            propsFilter={
              {
                legalEntityIds: [state.item?.employee?.legalEntity?.id],
              } as LegalEntityProjectsFilter
            }
            disabled={viewMode}
          />
          <TextField
            label={`${t(`${tRP}.fields.amount`)}`}
            type="number"
            value={state.item.amount}
            onChange={(e) => handleChangeField("amount", e.target.value)}
            InputProps={{
              inputProps: { min: 0.5, max: 12, step: 0.1 },
              endAdornment: <InputAdornment position="end">{"hours"}</InputAdornment>,
            }}
            onBlur={(e) => handleChangeField("amount", Number(e.target.value).toFixed(2))}
            disabled={viewMode}
          />
          <DesktopDatePicker
            inputFormat="dd.MM.yyyy"
            mask="__.__.____"
            label={`${t(`${tRP}.fields.tna_date`)}`}
            value={CustomDate.ToDate(state.item.tnADate)}
            onChange={(newValue) => {
              if (isValidDate(newValue)) {
                handleChangeField("tnADate", CustomDate.FromDate(newValue as Date));
              }
            }}
            renderInput={(params) => <TextField {...params} />}
            minDate={minDate}
            maxDate={maxDate}
            disabled={viewMode}
          />
        </DialogContent>
        <DialogActions
          handleCancel={
            // handleCloseDialog
            () => setState((state) => ({ ...state, CCDOpen: true }))
          }
          handleSave={() => {
            const diiferenceInSchedule: number | null =
              dataForThisDay.expectedPresence === 0
                ? null
                : (state.item?.amount ?? 0) +
                    dataForThisDay.alreadyReportedWorkedHours -
                    dataForThisDay.expectedPresence >
                  1
                ? (state.item?.amount ?? 0) +
                  dataForThisDay.alreadyReportedWorkedHours -
                  dataForThisDay.expectedPresence
                : (state.item?.amount ?? 0) +
                    dataForThisDay.alreadyReportedWorkedHours -
                    dataForThisDay.expectedPresence <
                  -1
                ? (state.item?.amount ?? 0) +
                  dataForThisDay.alreadyReportedWorkedHours -
                  dataForThisDay.expectedPresence
                : 0;

            diiferenceInSchedule === null || diiferenceInSchedule > 1 || diiferenceInSchedule < -1
              ? setState((state) => ({
                  ...state,
                  isWarningCCCDOpen: true,
                  diiferenceInSchedule: diiferenceInSchedule,
                }))
              : handleSaveWorkedHours();
          }}
          loading={state.loading}
          disabledSaveButton={viewMode}
          error={state.error}
        >
          {!!item && !viewMode && (
            <Button
              onClick={() => handleSaveWorkedHours(true)}
              style={{ backgroundColor: theme.palette.error.main, color: theme.palette.primary.contrastText }}
            >
              {`${t(`buttons.delete`)}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <CustomConfirmationChangesDialog
        open={state.isWarningCCCDOpen}
        loading={state.loading}
        title={"Outside of schedule"}
        handleCancel={() => {
          setState((state) => ({
            ...state,
            isWarningCCCDOpen: false,
            diiferenceInSchedule: 0,
          }));
        }}
        actionsAndHandlers={[
          {
            action: "Yes",
            buttonVariant: "contained",
            handler: () => {
              handleSaveWorkedHours();
            },
          },
        ]}
      >
        <Grid sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Grid>
            {(state.diiferenceInSchedule === null ||
              state.diiferenceInSchedule > 1 ||
              state.diiferenceInSchedule < -1) && (
              <>
                {state.diiferenceInSchedule === null ? (
                  <Typography>
                    <>
                      {state.item?.tnADate?.year && state.item?.tnADate?.month && state.item?.tnADate?.day && (
                        <>
                          We have noticed that you are reporting presence significantly off expected schedule. Do you
                          really want to save <b>{(+state.item.amount).toFixed(2)} hours</b> at{" "}
                          {intlFormatDateShort(
                            new Date(state.item.tnADate?.year, state.item.tnADate?.month - 1, state.item.tnADate?.day)
                          )}
                          {state.item?.client?.name && (
                            <>
                              {" "}
                              for the client <b>{state.item.client.name}</b>
                            </>
                          )}
                          {state.item?.project?.name && (
                            <>
                              {" "}
                              on project <b>{state.item.project.name}</b>
                            </>
                          )}
                          ?
                        </>
                      )}
                    </>
                  </Typography>
                ) : (
                  <>
                    <Grid>
                      <Typography>
                        We have noticed that you are reporting worked hours for this day that have more than one hour
                        difference:
                      </Typography>

                      <ul>
                        <li>
                          <Grid sx={{ display: "flex", flexDirection: "row" }}>
                            <Grid sx={{ width: "150px", minWidth: "150px" }}>
                              <Typography>
                                {state.item?.tnADate?.year &&
                                  state.item?.tnADate?.month &&
                                  state.item?.tnADate?.day &&
                                  intlFormatDateShort(
                                    new Date(
                                      state.item.tnADate?.year,
                                      state.item.tnADate?.month - 1,
                                      state.item.tnADate?.day
                                    )
                                  )}
                                :
                              </Typography>
                            </Grid>

                            <Grid sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                              <Grid sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                                <Grid sx={{ width: "100px", minWidth: "100px" }}>
                                  <Typography sx={{ fontWeight: "bold" }}>Already reported worked hours</Typography>
                                </Grid>

                                <Divider orientation="vertical" />

                                <Grid sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                                  {dataForThisDay.reportedWorkedHoursData.map((el, idx) => {
                                    const isLastElement = dataForThisDay.reportedWorkedHoursData.length - 1 === idx;

                                    return (
                                      <Typography key={idx}>
                                        {!!el?.amount && (
                                          <>
                                            <b>{el.amount.toFixed(2)} hours</b>
                                            {el?.client?.name && (
                                              <>
                                                {" "}
                                                for the client <b>{el.client.name}</b>
                                              </>
                                            )}
                                            {el?.project?.name && (
                                              <>
                                                {" "}
                                                on project <b>{el.project.name}</b>
                                              </>
                                            )}
                                            {`${isLastElement ? "" : ", "}`}
                                          </>
                                        )}
                                      </Typography>
                                    );
                                  })}
                                </Grid>
                              </Grid>

                              <Grid sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                                <Grid sx={{ width: "100px", minWidth: "100px" }}>
                                  <Typography sx={{ fontWeight: "bold" }}>Worked hours to save</Typography>
                                </Grid>

                                <Divider orientation="vertical" />

                                <Grid sx={{ display: "flex", flexDirection: "row", gap: "5px", flexWrap: "wrap" }}>
                                  <React.Fragment>
                                    {!!state.item?.amount && (
                                      <>
                                        <b>{(+state.item.amount).toFixed(2)} hours</b>
                                        {state.item?.client?.name && (
                                          <>
                                            {" "}
                                            for the client <b>{state.item.client.name}</b>
                                          </>
                                        )}
                                        {state.item?.project?.name && (
                                          <>
                                            {" "}
                                            on project <b>{state.item.project.name}</b>
                                          </>
                                        )}
                                      </>
                                    )}
                                  </React.Fragment>
                                </Grid>
                              </Grid>

                              <Grid sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                                <Grid sx={{ width: "100px", minWidth: "100px" }}>
                                  <Typography sx={{ fontWeight: "bold" }}>Reported worked hours(total):</Typography>
                                </Grid>

                                <Divider orientation="vertical" />

                                <Grid>
                                  <Typography sx={{ fontWeight: "bold" }}>
                                    {dataForThisDay.alreadyReportedWorkedHours + (state.item?.amount ?? 0)} hours
                                  </Typography>
                                </Grid>
                              </Grid>

                              <Grid sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                                <Grid sx={{ width: "100px", minWidth: "100px" }}>
                                  <Typography sx={{ fontWeight: "bold" }}>Expected presence:</Typography>
                                </Grid>

                                <Divider orientation="vertical" />

                                <Grid>
                                  <Typography sx={{ fontWeight: "bold" }}>
                                    {dataForThisDay.expectedPresence} hours
                                  </Typography>
                                </Grid>
                              </Grid>

                              <Grid sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                                <Grid sx={{ width: "100px", minWidth: "100px" }}>
                                  <Typography sx={{ fontWeight: "bold" }}>Difference:</Typography>
                                </Grid>

                                <Divider orientation="vertical" />

                                <Grid>
                                  <Typography sx={{ fontWeight: "bold" }}>
                                    {state.diiferenceInSchedule} hours
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        </li>
                      </ul>

                      <Typography>Do you really want to continue ?</Typography>
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </CustomConfirmationChangesDialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() =>
          setState((state) => ({
            ...state,
            openDialog: false,
            item: item ?? (DEFAULT_WORKED_HOURS as TnAWorkedHoursDTO),
            CCDOpen: false,
            error: "",
          }))
        }
      />
    </>
  );
};

export default React.memo(AddEditWorkedHours);
